import { useNavigate } from "react-router-dom";
import { useSettings } from "@/hooks/useSettings";
import Logo from "@/components/Logo";
import {
  ArrowLeft, Truck, Package, Globe, ShieldCheck, Clock,
  PackageSearch, AlertCircle, RotateCcw, CreditCard, XCircle,
  AlertTriangle, ShoppingBag, Headphones, Snowflake, MapPin,
} from "lucide-react";

const SectionCard = ({ icon: Icon, title, children, accent = "green" }) => (
  <div
    data-testid={`shipping-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
    className={`relative bg-[#0A0F1A]/70 border border-white/5 rounded-2xl p-6 backdrop-blur-sm transition-all hover:border-${
      accent === "blue" ? "blue-500/30" : "[#39FF14]/30"
    }`}
  >
    <div className="flex items-center gap-3 mb-4">
      <div
        className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
          accent === "blue"
            ? "bg-blue-500/10 border border-blue-500/30"
            : "bg-[#39FF14]/10 border border-[#39FF14]/30"
        }`}
      >
        <Icon className={`w-4 h-4 ${accent === "blue" ? "text-blue-400" : "text-[#39FF14]"}`} />
      </div>
      <h3 className="font-headline font-bold text-base text-white uppercase tracking-wider">
        {title}
      </h3>
    </div>
    <div className="text-sm text-zinc-300 leading-relaxed space-y-2.5">{children}</div>
  </div>
);

const TRUST = [
  { icon: Package, label: "Discreet Packaging" },
  { icon: PackageSearch, label: "Tracked Shipping" },
  { icon: ShieldCheck, label: "Quality Controlled" },
  { icon: Snowflake, label: "Secure Handling" },
  { icon: Headphones, label: "24h Support Response" },
];

export default function Shipping() {
  const navigate = useNavigate();
  const { whatsapp_supreme } = useSettings();
  const waDigits = (whatsapp_supreme || "").replace(/\D/g, "");

  return (
    <div className="min-h-screen bg-[#020409] text-white">
      <div className="border-b border-white/5 bg-black/60 backdrop-blur-md text-[11px] uppercase tracking-[0.2em] font-medium">
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            data-testid="shipping-back-btn"
            className="flex items-center gap-2 text-zinc-500 hover:text-white transition"
          >
            <ArrowLeft className="w-3 h-3 text-[#39FF14]" /> Back to Home
          </button>
        </div>
      </div>

      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#020409]/85 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="shipping-logo"><Logo /></button>
          <button
            onClick={() => navigate("/products")}
            className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-[#39FF14] transition"
          >
            ← Home
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(57,255,20,0.07),transparent_60%)] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-20">
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#39FF14] font-bold mb-3">Policies</p>
          <h1 className="font-headline text-5xl md:text-6xl font-black tracking-tighter mb-4">
            Shipping &{" "}
            <span className="text-[#39FF14]" style={{ textShadow: "0 0 18px rgba(57,255,20,0.5)" }}>
              Returns
            </span>
          </h1>
          <p className="text-zinc-400 leading-relaxed max-w-2xl">
            How we package, ship, track, and handle returns for every Supreme Laboratories order.
            Clear, fair, and aligned with research-grade standards.
          </p>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="px-6 py-8 border-b border-white/5">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {TRUST.map((t, i) => (
            <div
              key={i}
              data-testid={`trust-badge-${i}`}
              className="flex items-center gap-2.5 bg-[#0A0F1A] border border-white/5 rounded-lg px-4 py-3"
            >
              <div className="w-8 h-8 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/30 flex items-center justify-center shrink-0">
                <t.icon className="w-3.5 h-3.5 text-[#39FF14]" />
              </div>
              <span className="text-[11px] font-semibold text-zinc-200 leading-tight">{t.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* SHIPPING POLICY */}
      <section className="px-6 py-16">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.35em] text-blue-400 font-bold mb-3">Shipping Policy</p>
          <h2 className="font-headline text-3xl md:text-4xl font-black tracking-tighter mb-10">
            How we ship.
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            <SectionCard icon={Clock} title="Processing Time">
              <p>Orders are typically processed within <span className="text-[#39FF14] font-semibold">1–3 business days</span> after order confirmation.</p>
              <p className="text-zinc-400">Once processed, tracking information will be provided via email or direct contact.</p>
            </SectionCard>

            <SectionCard icon={Globe} title="Shipping Regions" accent="blue">
              <p>We currently ship to selected international destinations across <span className="text-blue-300 font-semibold">Europe</span> and other approved regions.</p>
              <p className="text-zinc-400">Customers are responsible for ensuring compliance with local regulations before placing an order.</p>
            </SectionCard>

            <SectionCard icon={Package} title="Packaging">
              <p>All products are packaged <span className="text-[#39FF14] font-semibold">discreetly and securely</span> to ensure product integrity during transit.</p>
              <p className="text-zinc-400">Temperature-sensitive products may be shipped with insulated packaging when required.</p>
            </SectionCard>

            <SectionCard icon={Truck} title="Delivery Time" accent="blue">
              <p className="mb-2 text-zinc-400">Estimated delivery times:</p>
              <ul className="space-y-1.5 text-zinc-300">
                <li className="flex justify-between border-b border-white/5 pb-1.5">
                  <span className="text-zinc-400">Germany</span>
                  <span className="font-semibold text-blue-300">2–5 business days</span>
                </li>
                <li className="flex justify-between border-b border-white/5 pb-1.5">
                  <span className="text-zinc-400">EU Countries</span>
                  <span className="font-semibold text-blue-300">4–10 business days</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-zinc-400">International</span>
                  <span className="font-semibold text-blue-300">7–21 business days</span>
                </li>
              </ul>
              <p className="text-xs text-zinc-500 mt-3">Delivery times may vary depending on customs clearance and local courier services.</p>
            </SectionCard>

            <SectionCard icon={MapPin} title="Tracking">
              <p>Tracking details will be provided once the shipment has been <span className="text-[#39FF14] font-semibold">dispatched</span>.</p>
            </SectionCard>

            <SectionCard icon={AlertCircle} title="Lost or Delayed Shipments" accent="blue">
              <p>If a shipment is significantly delayed or lost in transit, please contact our support team within <span className="text-blue-300 font-semibold">14 days</span> of the estimated delivery date.</p>
            </SectionCard>
          </div>
        </div>
      </section>

      {/* RETURNS */}
      <section className="px-6 py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#39FF14] font-bold mb-3">Returns &amp; Refunds</p>
          <h2 className="font-headline text-3xl md:text-4xl font-black tracking-tighter mb-10">
            Returns &amp; refunds.
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            <SectionCard icon={RotateCcw} title="Return Eligibility">
              <p>Due to the sensitive nature of research compounds and laboratory materials, <span className="text-[#39FF14] font-semibold">returns are generally not accepted</span> once an order has been shipped.</p>
            </SectionCard>

            <SectionCard icon={ShoppingBag} title="Damaged or Incorrect Items" accent="blue">
              <p>If you receive a damaged, defective, or incorrect item, contact us within <span className="text-blue-300 font-semibold">48 hours of delivery</span> with:</p>
              <ul className="list-disc list-inside text-zinc-400 ml-1">
                <li>Order number</li>
                <li>Photos of the package and product</li>
                <li>Description of the issue</li>
              </ul>
              <p className="text-zinc-400">Our support team will review the case and provide an appropriate resolution.</p>
            </SectionCard>

            <SectionCard icon={CreditCard} title="Refund Policy">
              <p className="text-zinc-400 mb-1">Refunds may be issued in cases of:</p>
              <ul className="list-disc list-inside text-zinc-300 ml-1">
                <li>Incorrect shipment</li>
                <li>Products damaged during transit</li>
                <li>Lost packages confirmed by the carrier</li>
              </ul>
              <p>Approved refunds are processed within <span className="text-[#39FF14] font-semibold">5–10 business days</span>.</p>
            </SectionCard>

            <SectionCard icon={XCircle} title="Order Cancellation" accent="blue">
              <p>Orders may only be cancelled <span className="text-blue-300 font-semibold">before shipment processing begins</span>.</p>
            </SectionCard>
          </div>

          {/* IMPORTANT NOTICE */}
          <div
            data-testid="shipping-important-notice"
            className="mt-10 flex flex-col sm:flex-row items-center gap-4 sm:gap-5 rounded-2xl border border-red-500/40 bg-red-500/[0.06] px-6 py-5 backdrop-blur-sm"
          >
            <div className="w-12 h-12 rounded-full bg-red-500/15 border border-red-500/50 flex items-center justify-center shrink-0 shadow-[0_0_18px_rgba(239,68,68,0.4)]">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div className="text-center sm:text-left">
              <p className="font-headline text-base font-black uppercase tracking-[0.2em] text-red-400 leading-tight">
                Important Notice
              </p>
              <p className="text-sm text-zinc-300 leading-relaxed mt-1">
                All products are sold <span className="text-red-300 font-semibold">strictly for laboratory and research purposes only</span>.
                Not intended for human consumption, medical use, or diagnostic applications.
              </p>
            </div>
          </div>

          {/* CONTACT CTA */}
          <div className="mt-10 bg-[#0A0F1A] border border-white/5 rounded-2xl p-8 text-center">
            <Headphones className="w-9 h-9 text-[#39FF14] mx-auto mb-3" />
            <h3 className="font-headline text-2xl font-black mb-2">Need help with a shipment?</h3>
            <p className="text-zinc-400 text-sm mb-5 max-w-xl mx-auto">
              Our team replies within 24 hours on business days. Please include your order ID.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a
                href="mailto:contact@supremelaboratories.com"
                data-testid="shipping-cta-email"
                className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#2adc0c] text-black font-bold uppercase tracking-wider text-xs px-5 py-3 rounded-md shadow-[0_0_15px_rgba(57,255,20,0.4)] transition"
              >
                contact@supremelaboratories.com
              </a>
              <a
                href={`https://wa.me/${waDigits}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="shipping-cta-whatsapp"
                className="inline-flex items-center gap-2 bg-transparent border border-white/15 hover:border-[#39FF14]/50 text-white hover:text-[#39FF14] font-bold uppercase tracking-wider text-xs px-5 py-3 rounded-md transition"
              >
                WhatsApp · +{waDigits}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
